import { useMemo, useState } from 'react';
import { Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/toaster';
import { usePermissions } from '@/hooks/use-permissions';
import { useUsers } from '@/hooks/use-users';
import {
  useAddWorkGroupMember,
  useRemoveWorkGroupMember,
  useWorkGroupMembers,
  workgroupsMutationErrorMessage,
} from '@/hooks/use-workgroups';

interface WorkGroupMembersPanelProps {
  workGroupId: string;
}

export function WorkGroupMembersPanel({ workGroupId }: WorkGroupMembersPanelProps) {
  const { hasCapability } = usePermissions();
  const canManage = hasCapability('workgroups.members.manage');

  const { data: members, isLoading, error } = useWorkGroupMembers(workGroupId, canManage);
  const { data: users = [], isLoading: usersLoading } = useUsers();

  const addMutation = useAddWorkGroupMember();
  const removeMutation = useRemoveWorkGroupMember();

  const [selectedUserId, setSelectedUserId] = useState('');
  const [manualUserId, setManualUserId] = useState('');

  const internalUsers = useMemo(
    () => users.filter((u) => u.role !== 'client' && u.isActive),
    [users],
  );

  const userById = useMemo(() => new Map(internalUsers.map((u) => [u.id, u])), [internalUsers]);

  const memberUserIds = new Set(members?.map((m) => m.userId) ?? []);
  const availableUsers = internalUsers.filter((u) => !memberUserIds.has(u.id));

  const onError = (err: unknown) => {
    toast({ title: workgroupsMutationErrorMessage(err), variant: 'destructive' });
  };

  const handleAdd = () => {
    const userId = selectedUserId || manualUserId.trim();
    if (!userId) {
      toast({ title: 'Select or enter a user ID', variant: 'destructive' });
      return;
    }
    addMutation.mutate(
      { workGroupId, userId },
      {
        onSuccess: () => {
          toast({ title: 'Member added' });
          setSelectedUserId('');
          setManualUserId('');
        },
        onError,
      },
    );
  };

  const handleRemove = (userId: string, label: string) => {
    if (!window.confirm(`Remove ${label} from this work group?`)) return;
    removeMutation.mutate(
      { workGroupId, userId },
      {
        onSuccess: () => toast({ title: 'Member removed' }),
        onError,
      },
    );
  };

  if (!canManage) {
    return null;
  }

  return (
    <Card className="border-border">
      <CardHeader className="space-y-1">
        <div className="flex items-center gap-2 text-foreground">
          <Users className="h-4 w-4" aria-hidden />
          <CardTitle className="text-base">Members</CardTitle>
        </div>
        <CardDescription>
          Standalone work group membership. Clients cannot be added. Folder access remains
          backend-authoritative.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? <p className="text-sm text-muted-foreground">Loading members…</p> : null}
        {error ? (
          <p className="text-sm text-destructive">Could not load work group members.</p>
        ) : null}

        {members && members.length === 0 && !isLoading ? (
          <p className="text-sm text-muted-foreground">No members yet.</p>
        ) : null}

        {members && members.length > 0 ? (
          <ul className="space-y-2">
            {members.map((member) => {
              const user = userById.get(member.userId);
              const label = user?.displayName?.trim() || member.email;
              const isOwner = member.role === 'owner';

              return (
                <li
                  key={member.id}
                  className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-foreground">{label}</p>
                    <p className="truncate text-xs text-muted-foreground">{member.email}</p>
                  </div>
                  <Badge variant={isOwner ? 'secondary' : 'outline'} className="font-normal capitalize">
                    {member.role}
                  </Badge>
                  {!isOwner ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive"
                      disabled={removeMutation.isPending}
                      onClick={() => handleRemove(member.userId, label)}
                    >
                      Remove
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}

        <div className="space-y-3 border-t border-border pt-3">
          <p className="text-sm font-medium text-foreground">Add member</p>
          {usersLoading ? (
            <p className="text-sm text-muted-foreground">Loading users…</p>
          ) : availableUsers.length > 0 ? (
            <div className="space-y-2">
              <Label htmlFor="wg-member-user">Internal user</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger id="wg-member-user">
                  <SelectValue placeholder="Select internal user" />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.displayName || u.email} ({u.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="wg-member-user-id">User ID</Label>
              <Input
                id="wg-member-user-id"
                value={manualUserId}
                onChange={(e) => setManualUserId(e.target.value)}
                placeholder="UUID of internal user"
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                No unassigned internal users in the picker, or user list unavailable. Client users
                are rejected by the backend.
              </p>
            </div>
          )}
          <Button
            type="button"
            size="sm"
            disabled={
              addMutation.isPending || usersLoading || (!selectedUserId && !manualUserId.trim())
            }
            onClick={handleAdd}
          >
            Add member
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
